import { usePageTitle } from '../hooks/usePageTitle';
import React, { useState, useEffect, useCallback } from 'react';
import api from '../api';
import { useTheme } from '../contexts/ThemeContext';

interface Salon { id: string; ad: string; kapasite: number; }
interface Etkinlik {
  id: string; ad: string; tip: string; aciklama: string | null;
  tekrarlayan: boolean; tarih: string | null;
  baslangic_saat: string; bitis_saat: string; tum_gun: boolean;
  kiralayan: string | null; kira_ucreti: number | null;
  bilet_aktif: boolean; bilet_fiyati: number | null; kapasite: number | null;
  aktif: boolean; iptal_edildi: boolean; olusturuldu: string;
  salonlar: Salon[]; gunler: string[]; bilet_sayisi: number;
}
interface Bilet {
  id: string; etkinlik_id: string; tarih: string | null;
  ad: string; soyad: string; email: string | null; tel: string | null;
  odeme_durumu: string; tutar: number | null; notlar: string | null;
  olusturuldu: string; ogrenci_id: string | null;
}
interface Ogrenci { id: string; ad: string; soyad: string; }

const TIP_RENK: Record<string, { text: string; bg: string }> = {
  milonga:  { text: '#6B46C1', bg: '#F3F0FF' },
  pratik:   { text: '#2563EB', bg: '#EFF6FF' },
  festival: { text: '#BE185D', bg: '#FDF2F8' },
  kiralama: { text: '#EA580C', bg: '#FFF7ED' },
  diger:    { text: '#6B7280', bg: '#F3F4F6' },
};
const TIP_LABEL: Record<string, string> = { milonga: 'Milonga', pratik: 'Pratik', festival: 'Festival', kiralama: 'Kiralama', diger: 'Diğer' };
const TP_UYGUN_TIPLER = ['milonga', 'pratik', 'festival'];
const GUNLER = ['Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt', 'Paz'];
const ODEME_RENK: Record<string, string> = { bekliyor: '#F59E0B', odendi: '#10B981', iptal: '#EF4444' };
const ODEME_BG: Record<string, string> = { bekliyor: '#FEF3C7', odendi: '#D1FAE5', iptal: '#FEE2E2' };
const ODEME_LABEL: Record<string, string> = { bekliyor: 'Bekliyor', odendi: 'Ödendi', iptal: 'İptal' };

const BOSH_FORM = () => ({
  ad: '', tip: 'milonga', aciklama: '',
  tekrarlayan: false, tarih: '', baslangic_saat: '20:00', bitis_saat: '23:00',
  tum_gun: false, kiralayan: '', kira_ucreti: '',
  bilet_aktif: false, bilet_fiyati: '', kapasite: '',
  salon_idler: [] as string[], gunler: [] as string[],
});

const BOSH_BILET = () => ({
  ad: '', soyad: '', email: '', tel: '',
  tutar: '', tarih: '', notlar: '', odeme_durumu: 'odendi',
  ogrenci_id: '' as string, tp_kullan: false,
});

const inpSt: React.CSSProperties = {
  width: '100%', border: '1px solid #e0e0e0', borderRadius: 6,
  padding: '8px 10px', fontSize: 13, outline: 'none', boxSizing: 'border-box',
};
const lblSt: React.CSSProperties = {
  fontSize: 12, color: '#555', fontWeight: 500, marginBottom: 4, display: 'block',
};

function formatSaat(s: string) { return s ? s.slice(0, 5) : ''; }
function formatTarih(d: string | null) {
  if (!d) return '';
  return new Date(d + 'T12:00:00').toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' });
}

function TipBadge({ tip }: { tip: string }) {
  const r = TIP_RENK[tip] || TIP_RENK.diger;
  return (
    <span style={{
      display: 'inline-block', fontSize: 10, fontWeight: 600,
      padding: '2px 8px', borderRadius: 20, background: r.bg, color: r.text, letterSpacing: 0.3,
    }}>
      {TIP_LABEL[tip] || tip}
    </span>
  );
}

function InfoKutu({ baslik, deger, isDark }: { baslik: string; deger: string | number | null | undefined; isDark?: boolean }) {
  return (
    <div>
      <div style={{ fontSize: 10, color: isDark ? '#666' : '#aaa', fontWeight: 500, letterSpacing: 0.3, textTransform: 'uppercase', marginBottom: 2 }}>{baslik}</div>
      <div style={{ fontSize: 12, color: isDark ? '#e0e0e0' : '#333', fontWeight: 500 }}>{deger || '—'}</div>
    </div>
  );
}

function Toggle({ on, onChange, label, isDark }: { on: boolean; onChange: (v: boolean) => void; label: string; isDark?: boolean }) {
  return (
    <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontSize: 13, color: isDark ? '#e0e0e0' : '#333', userSelect: 'none' }}>
      <div
        onClick={() => onChange(!on)}
        style={{
          width: 38, height: 20, borderRadius: 10,
          background: on ? '#3C3489' : '#ddd',
          position: 'relative', cursor: 'pointer', transition: 'background 0.2s', flexShrink: 0,
        }}
      >
        <div style={{
          position: 'absolute', top: 2, left: on ? 20 : 2,
          width: 16, height: 16, borderRadius: '50%', background: 'white',
          transition: 'left 0.2s',
        }} />
      </div>
      {label}
    </label>
  );
}

const Etkinlikler: React.FC = () => {
  usePageTitle('Etkinlikler');
  const { isDark } = useTheme();
  const card        = isDark ? '#1a1a1a' : 'white';
  const cardAlt     = isDark ? '#1e1e1e' : '#f8f8f8';
  const cardHover   = isDark ? '#252525' : '#fafafa';
  const border      = isDark ? '#2a2a2a' : '#eee';
  const borderSub   = isDark ? '#202020' : '#f0f0f0';
  const textPrimary = isDark ? '#e8e8e8' : '#1a1a1a';
  const textSec     = isDark ? '#999'    : '#888';
  const textMuted   = isDark ? '#666'    : '#aaa';
  const [etkinlikler, setEtkinlikler] = useState<Etkinlik[]>([]);
  const [salonlar, setSalonlar] = useState<Salon[]>([]);
  const [yukleniyor, setYukleniyor] = useState(true);
  const [filtre, setFiltre] = useState('hepsi');

  const [modal, setModal] = useState(false);
  const [duzenle, setDuzenle] = useState<Etkinlik | null>(null);
  const [form, setForm] = useState(BOSH_FORM());
  const [kaydiyor, setKaydiyor] = useState(false);
  const [hata, setHata] = useState('');
  const [cakisma, setCakisma] = useState<string[]>([]);

  const [secilen, setSecilen] = useState<Etkinlik | null>(null);
  const [biletler, setBiletler] = useState<Bilet[]>([]);
  const [biletYukleniyor, setBiletYukleniyor] = useState(false);
  const [biletEkleAcik, setBiletEkleAcik] = useState(false);
  const [biletForm, setBiletForm] = useState(BOSH_BILET());
  const [biletKaydiyor, setBiletKaydiyor] = useState(false);
  const [biletHata, setBiletHata] = useState('');
  const [ogrenciler, setOgrenciler] = useState<Ogrenci[]>([]);
  const [ogrenciArama, setOgrenciArama] = useState('');
  const [ogrenciTpBakiye, setOgrenciTpBakiye] = useState<number>(0);

  const [katilimlar, setKatilimlar] = useState<any[]>([]);
  const [katilimOzet, setKatilimOzet] = useState<{ toplam_kisi: number; toplam_gelir: number; seans_sayisi: number } | null>(null);
  const [katilimYukleniyor, setKatilimYukleniyor] = useState(false);

  const yukle = useCallback(() => {
    setYukleniyor(true);
    Promise.all([
      api.get('/api/etkinlikler').catch(() => ({ data: [] })),
      api.get('/api/salonlar').catch(() => ({ data: [] })),
    ])
      .then(([e, s]) => { setEtkinlikler(e.data); setSalonlar(s.data); })
      .finally(() => setYukleniyor(false));
  }, []);

  useEffect(() => { yukle(); }, [yukle]);

  const biletYukle = async (id: string) => {
    setBiletYukleniyor(true);
    try {
      const r = await api.get(`/api/etkinlikler/${id}`);
      setBiletler(r.data.biletler || []);
    } finally { setBiletYukleniyor(false); }
  };

  const setF = (k: string, v: any) => setForm(prev => ({ ...prev, [k]: v }));

  const toggleSalon = (id: string) =>
    setForm(prev => ({
      ...prev,
      salon_idler: prev.salon_idler.includes(id)
        ? prev.salon_idler.filter(x => x !== id)
        : [...prev.salon_idler, id],
    }));

  const toggleGun = (g: string) =>
    setForm(prev => ({
      ...prev,
      gunler: prev.gunler.includes(g)
        ? prev.gunler.filter(x => x !== g)
        : [...prev.gunler, g],
    }));

  const acModal = (etkinlik?: Etkinlik) => {
    setHata(''); setCakisma([]);
    if (etkinlik) {
      setDuzenle(etkinlik);
      setForm({
        ad: etkinlik.ad, tip: etkinlik.tip,
        aciklama: etkinlik.aciklama || '',
        tekrarlayan: etkinlik.tekrarlayan,
        tarih: etkinlik.tarih || '',
        baslangic_saat: formatSaat(etkinlik.baslangic_saat),
        bitis_saat: formatSaat(etkinlik.bitis_saat),
        tum_gun: etkinlik.tum_gun,
        kiralayan: etkinlik.kiralayan || '',
        kira_ucreti: etkinlik.kira_ucreti?.toString() || '',
        bilet_aktif: etkinlik.bilet_aktif,
        bilet_fiyati: etkinlik.bilet_fiyati?.toString() || '',
        kapasite: etkinlik.kapasite?.toString() || '',
        salon_idler: etkinlik.salonlar.map(s => s.id),
        gunler: etkinlik.gunler || [],
      });
    } else {
      setDuzenle(null);
      setForm(BOSH_FORM());
    }
    setModal(true);
  };

  const kaydet = async (force = false) => {
    if (!form.ad.trim()) { setHata('Etkinlik adı zorunlu'); return; }
    if (!form.salon_idler.length) { setHata('En az bir salon seçin'); return; }
    if (!form.tekrarlayan && !form.tarih) { setHata('Tarih giriniz'); return; }
    if (form.tekrarlayan && !form.gunler.length) { setHata('En az bir gün seçin'); return; }

    setKaydiyor(true); setHata('');
    try {
      const payload = {
        ...form,
        kira_ucreti: form.kira_ucreti ? parseFloat(form.kira_ucreti as string) : null,
        bilet_fiyati: form.bilet_fiyati ? parseFloat(form.bilet_fiyati as string) : null,
        kapasite: form.kapasite ? parseInt(form.kapasite as string) : null,
        force,
      };
      if (duzenle) await api.put(`/api/etkinlikler/${duzenle.id}`, payload);
      else await api.post('/api/etkinlikler', payload);
      setModal(false);
      yukle();
    } catch (err: any) {
      if (err.response?.status === 409 && err.response?.data?.cakismalar) {
        setCakisma(err.response.data.cakismalar);
      } else {
        setHata(err.response?.data?.hata || 'Bir hata oluştu');
      }
    } finally { setKaydiyor(false); }
  };

  const sil = async (e: Etkinlik) => {
    if (!window.confirm(`"${e.ad}" etkinliğini silmek istediğinizden emin misiniz?`)) return;
    await api.delete(`/api/etkinlikler/${e.id}`);
    if (secilen?.id === e.id) setSecilen(null);
    yukle();
  };

  const katilimYukle = async (id: string) => {
    setKatilimYukleniyor(true);
    try {
      const r = await api.get(`/api/etkinlikler/${id}/katilim`);
      setKatilimlar(r.data.kayitlar || []);
      setKatilimOzet(r.data.ozet || null);
    } catch { setKatilimlar([]); setKatilimOzet(null); }
    finally { setKatilimYukleniyor(false); }
  };

  const secilenAc = (etkinlik: Etkinlik) => {
    setSecilen(etkinlik);
    setBiletEkleAcik(false);
    setBiletForm(BOSH_BILET());
    setBiletHata('');
    biletYukle(etkinlik.id);
    if (['milonga', 'pratik', 'festival'].includes(etkinlik.tip)) katilimYukle(etkinlik.id);
    else { setKatilimlar([]); setKatilimOzet(null); }
  };

  const biletEkleAc = async () => {
    setBiletEkleAcik(p => !p); setBiletForm(BOSH_BILET()); setBiletHata(''); setOgrenciArama(''); setOgrenciTpBakiye(0);
    if (!ogrenciler.length) {
      const r = await api.get('/api/ogrenciler?limit=500').catch(() => ({ data: { ogrenciler: [] } }));
      setOgrenciler(r.data.ogrenciler || r.data || []);
    }
  };

  const ogrenciSec = async (o: Ogrenci) => {
    setBiletForm(p => ({ ...p, ogrenci_id: o.id }));
    setOgrenciArama(`${o.ad} ${o.soyad}`);
    const r = await api.get('/api/tp/bakiye', { params: { ogrenci_id: o.id } }).catch(() => ({ data: { bakiye: 0 } }));
    setOgrenciTpBakiye(r.data.bakiye || 0);
  };

  const biletKaydet = async () => {
    if (!biletForm.ad.trim() || !biletForm.soyad.trim()) { setBiletHata('Ad ve soyad zorunlu'); return; }
    setBiletKaydiyor(true); setBiletHata('');
    try {
      await api.post(`/api/etkinlikler/${secilen!.id}/biletler`, {
        ...biletForm,
        tutar: biletForm.tutar ? parseFloat(biletForm.tutar as string) : null,
        ogrenci_id: biletForm.ogrenci_id || null,
        tp_kullan: biletForm.tp_kullan ? ogrenciTpBakiye : 0,
      });
      await biletYukle(secilen!.id);
      setBiletEkleAcik(false);
      setBiletForm(BOSH_BILET());
      yukle();
    } catch (err: any) {
      setBiletHata(err.response?.data?.hata || 'Bir hata oluştu');
    } finally { setBiletKaydiyor(false); }
  };

  const biletSil = async (bilet_id: string) => {
    if (!window.confirm('Bu bileti silmek istediğinizden emin misiniz?')) return;
    await api.delete(`/api/etkinlikler/${secilen!.id}/biletler/${bilet_id}`);
    setBiletler(prev => prev.filter(b => b.id !== bilet_id));
  };

  const filtreli = filtre === 'hepsi' ? etkinlikler : etkinlikler.filter(e => e.tip === filtre);
  const surekliler = filtreli.filter(e => e.tekrarlayan);
  const tekSeferlikler = filtreli
    .filter(e => !e.tekrarlayan)
    .sort((a, b) => (a.tarih || '').localeCompare(b.tarih || ''));

  const EtkinlikSatir = ({ e }: { e: Etkinlik }) => (
    <div
      onClick={() => secilenAc(e)}
      style={{
        display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px',
        borderBottom: `1px solid ${borderSub}`, cursor: 'pointer',
        background: secilen?.id === e.id ? isDark ? 'rgba(83,74,183,0.2)' : '#F5F4FF' : card,
      }}
      onMouseEnter={ev => { if (secilen?.id !== e.id) (ev.currentTarget as HTMLDivElement).style.background = cardHover; }}
      onMouseLeave={ev => { (ev.currentTarget as HTMLDivElement).style.background = secilen?.id === e.id ? isDark ? 'rgba(83,74,183,0.2)' : '#F5F4FF' : card; }}
    >
      <TipBadge tip={e.tip} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 500, fontSize: 13, color: textPrimary, marginBottom: 2 }}>
          {e.ad}
          {e.iptal_edildi && (
            <span style={{ marginLeft: 6, fontSize: 10, color: '#EF4444', fontWeight: 600 }}>İPTAL</span>
          )}
        </div>
        <div style={{ fontSize: 11, color: textSec, display: 'flex', flexWrap: 'wrap', gap: '3px 8px' }}>
          <span>{e.salonlar.map(s => s.ad).join(', ')}</span>
          {e.tekrarlayan
            ? <span>· {e.gunler.join(', ')} · {formatSaat(e.baslangic_saat)}–{formatSaat(e.bitis_saat)}</span>
            : <span>· {formatTarih(e.tarih)} · {e.tum_gun ? 'Tüm gün' : `${formatSaat(e.baslangic_saat)}–${formatSaat(e.bitis_saat)}`}</span>
          }
          {e.kiralayan && <span>· {e.kiralayan}</span>}
        </div>
      </div>
      {e.bilet_aktif && (
        <div style={{ fontSize: 11, color: '#6B46C1', fontWeight: 500, whiteSpace: 'nowrap', flexShrink: 0 }}>
          🎟 {e.bilet_sayisi}
        </div>
      )}
      <div style={{ display: 'flex', gap: 6, flexShrink: 0 }} onClick={ev => ev.stopPropagation()}>
        <button
          onClick={() => acModal(e)}
          style={{ border: `1px solid ${border}`, background: card, borderRadius: 5, padding: '4px 10px', fontSize: 11, cursor: 'pointer', color: isDark ? '#bbb' : '#555' }}
        >Düzenle</button>
        <button
          onClick={() => sil(e)}
          style={{ border: '1px solid #fca5a5', background: '#fff5f5', borderRadius: 5, padding: '4px 10px', fontSize: 11, cursor: 'pointer', color: '#EF4444' }}
        >Sil</button>
      </div>
    </div>
  );

  return (
    <div style={{ padding: 24 }}>
      {/* Başlık */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20 }}>
        <div>
          <div style={{ fontSize: 20, fontWeight: 700, color: textPrimary }}>Etkinlikler</div>
          <div style={{ fontSize: 12, color: textSec, marginTop: 2 }}>Milonga, pratik, salon kiralamaları ve diğer etkinlikler</div>
        </div>
        <button
          onClick={() => acModal()}
          style={{ background: '#3C3489', color: 'white', border: 'none', borderRadius: 8, padding: '9px 16px', fontSize: 13, fontWeight: 500, cursor: 'pointer' }}
        >
          + Yeni Etkinlik
        </button>
      </div>

      {/* Filtre tabları */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 20 }}>
        {['hepsi', 'milonga', 'pratik', 'festival', 'kiralama', 'diger'].map(f => (
          <button
            key={f}
            onClick={() => setFiltre(f)}
            style={{
              border: `1px solid ${filtre === f ? '#3C3489' : '#e0e0e0'}`,
              background: filtre === f ? '#3C3489' : card,
              color: filtre === f ? 'white' : isDark ? '#999' : '#666',
              borderRadius: 6, padding: '6px 14px', fontSize: 12,
              cursor: 'pointer', fontWeight: filtre === f ? 600 : 400,
            }}
          >
            {f === 'hepsi' ? 'Hepsi' : TIP_LABEL[f]}
          </button>
        ))}
      </div>

      {yukleniyor ? (
        <div style={{ textAlign: 'center', padding: 60, color: textMuted }}>Yükleniyor...</div>
      ) : etkinlikler.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 60, color: textMuted }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>🎭</div>
          <div style={{ fontSize: 14 }}>Henüz etkinlik eklenmemiş</div>
          <button
            onClick={() => acModal()}
            style={{ marginTop: 16, background: '#3C3489', color: 'white', border: 'none', borderRadius: 8, padding: '8px 16px', fontSize: 13, cursor: 'pointer' }}
          >
            İlk etkinliği ekle
          </button>
        </div>
      ) : (
        <div>
          {surekliler.length > 0 && (
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: textSec, letterSpacing: 0.5, textTransform: 'uppercase', marginBottom: 8 }}>
                Sürekli Etkinlikler ({surekliler.length})
              </div>
              <div style={{ background: card, borderRadius: 10, border: `1px solid ${border}`, overflow: 'hidden' }}>
                {surekliler.map(e => <EtkinlikSatir key={e.id} e={e} />)}
              </div>
            </div>
          )}
          {tekSeferlikler.length > 0 && (
            <div>
              <div style={{ fontSize: 11, fontWeight: 600, color: textSec, letterSpacing: 0.5, textTransform: 'uppercase', marginBottom: 8 }}>
                Tek Seferlik Etkinlikler ({tekSeferlikler.length})
              </div>
              <div style={{ background: card, borderRadius: 10, border: `1px solid ${border}`, overflow: 'hidden' }}>
                {tekSeferlikler.map(e => <EtkinlikSatir key={e.id} e={e} />)}
              </div>
            </div>
          )}
          {filtreli.length === 0 && (
            <div style={{ textAlign: 'center', padding: 40, color: '#aaa', fontSize: 13 }}>Bu filtrede etkinlik yok</div>
          )}
        </div>
      )}

      {/* Sağ çekmece */}
      {secilen && (
        <>
          <div
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.2)', zIndex: 200 }}
            onClick={() => setSecilen(null)}
          />
          <div style={{
            position: 'fixed', right: 0, top: 0, bottom: 0, width: 420,
            background: 'white', zIndex: 201, overflow: 'auto',
            boxShadow: '-4px 0 24px rgba(0,0,0,0.12)',
          }}>
            {/* Başlık */}
            <div style={{ padding: '18px 20px', borderBottom: '1px solid #eee', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
              <div>
                <TipBadge tip={secilen.tip} />
                <div style={{ fontSize: 16, fontWeight: 700, color: '#222', marginTop: 6 }}>{secilen.ad}</div>
                {secilen.iptal_edildi && (
                  <span style={{ fontSize: 11, color: '#EF4444', fontWeight: 600 }}>İPTAL EDİLDİ</span>
                )}
              </div>
              <button
                onClick={() => setSecilen(null)}
                style={{ border: 'none', background: 'none', fontSize: 20, cursor: 'pointer', color: '#aaa', lineHeight: 1 }}
              >×</button>
            </div>

            {/* Detaylar */}
            <div style={{ padding: '16px 20px', borderBottom: '1px solid #eee' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <InfoKutu baslik="Salonlar" deger={secilen.salonlar.map(s => s.ad).join(', ')} />
                {secilen.tekrarlayan
                  ? <InfoKutu baslik="Günler" deger={secilen.gunler.join(', ')} />
                  : <InfoKutu baslik="Tarih" deger={formatTarih(secilen.tarih)} />
                }
                <InfoKutu
                  baslik="Saat"
                  deger={secilen.tum_gun ? 'Tüm gün' : `${formatSaat(secilen.baslangic_saat)} – ${formatSaat(secilen.bitis_saat)}`}
                />
                {secilen.kapasite != null && <InfoKutu baslik="Kapasite" deger={`${secilen.kapasite} kişi`} />}
                {secilen.kiralayan && <InfoKutu baslik="Kiralayan" deger={secilen.kiralayan} />}
                {secilen.kira_ucreti != null && <InfoKutu baslik="Kira Ücreti" deger={`${secilen.kira_ucreti} ₺`} />}
              </div>
              {secilen.aciklama && (
                <div style={{ marginTop: 12, fontSize: 12, color: '#666', lineHeight: 1.6 }}>{secilen.aciklama}</div>
              )}
            </div>

            {/* Biletler */}
            {secilen.bilet_aktif ? (
              <div style={{ padding: '16px 20px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#333' }}>
                    Biletler
                    {biletler.filter(b => b.odeme_durumu !== 'iptal').length > 0 && (
                      <span style={{
                        marginLeft: 6, fontSize: 11, background: '#F3F0FF',
                        color: '#6B46C1', borderRadius: 10, padding: '1px 7px', fontWeight: 600,
                      }}>
                        {biletler.filter(b => b.odeme_durumu !== 'iptal').length}
                      </span>
                    )}
                    {secilen.bilet_fiyati != null && (
                      <span style={{ marginLeft: 8, fontSize: 11, color: '#888', fontWeight: 400 }}>
                        · {secilen.bilet_fiyati} ₺
                      </span>
                    )}
                  </div>
                  <button
                    onClick={biletEkleAc}
                    style={{
                      border: '1px solid #3C3489', background: 'white', color: '#3C3489',
                      borderRadius: 6, padding: '5px 12px', fontSize: 11, cursor: 'pointer', fontWeight: 500,
                    }}
                  >
                    {biletEkleAcik ? 'İptal' : '+ Bilet Ekle'}
                  </button>
                </div>

                {biletEkleAcik && (
                  <div style={{ background: '#fafafa', borderRadius: 8, padding: 14, marginBottom: 14, border: '1px solid #eee' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                      <div>
                        <label style={lblSt}>Ad *</label>
                        <input style={inpSt} value={biletForm.ad} onChange={ev => setBiletForm(p => ({ ...p, ad: ev.target.value }))} placeholder="Ad" />
                      </div>
                      <div>
                        <label style={lblSt}>Soyad *</label>
                        <input style={inpSt} value={biletForm.soyad} onChange={ev => setBiletForm(p => ({ ...p, soyad: ev.target.value }))} placeholder="Soyad" />
                      </div>
                      <div>
                        <label style={lblSt}>E-posta</label>
                        <input style={inpSt} value={biletForm.email} onChange={ev => setBiletForm(p => ({ ...p, email: ev.target.value }))} placeholder="E-posta" />
                      </div>
                      <div>
                        <label style={lblSt}>Tel</label>
                        <input style={inpSt} value={biletForm.tel} onChange={ev => setBiletForm(p => ({ ...p, tel: ev.target.value }))} placeholder="Telefon" />
                      </div>
                      <div>
                        <label style={lblSt}>Ücret (₺)</label>
                        <input
                          style={inpSt} type="number" value={biletForm.tutar}
                          onChange={ev => setBiletForm(p => ({ ...p, tutar: ev.target.value }))}
                          placeholder={secilen.bilet_fiyati?.toString() || ''}
                        />
                      </div>
                      <div>
                        <label style={lblSt}>Ödeme</label>
                        <select style={inpSt} value={biletForm.odeme_durumu} onChange={ev => setBiletForm(p => ({ ...p, odeme_durumu: ev.target.value }))}>
                          <option value="odendi">Ödendi</option>
                          <option value="bekliyor">Bekliyor</option>
                          <option value="iptal">İptal</option>
                        </select>
                      </div>
                      {secilen.tekrarlayan && (
                        <div style={{ gridColumn: '1/-1' }}>
                          <label style={lblSt}>Seans Tarihi</label>
                          <input style={inpSt} type="date" value={biletForm.tarih} onChange={ev => setBiletForm(p => ({ ...p, tarih: ev.target.value }))} />
                        </div>
                      )}
                      <div style={{ gridColumn: '1/-1' }}>
                        <label style={lblSt}>Not</label>
                        <input style={inpSt} value={biletForm.notlar} onChange={ev => setBiletForm(p => ({ ...p, notlar: ev.target.value }))} placeholder="Opsiyonel not" />
                      </div>

                      {secilen && TP_UYGUN_TIPLER.includes(secilen.tip) && (
                        <div style={{ gridColumn: '1/-1' }}>
                          <label style={lblSt}>Öğrenci (opsiyonel — TP kazanımı/harcaması için)</label>
                          <input
                            style={inpSt} value={ogrenciArama}
                            onChange={ev => { setOgrenciArama(ev.target.value); setBiletForm(p => ({ ...p, ogrenci_id: '' })); setOgrenciTpBakiye(0); }}
                            placeholder="Ad, soyad ara..."
                          />
                          {ogrenciArama.length >= 2 && !biletForm.ogrenci_id && (
                            <div style={{ border: `1px solid ${border}`, borderRadius: 8, maxHeight: 160, overflowY: 'auto', marginTop: 4 }}>
                              {ogrenciler.filter(o => `${o.ad} ${o.soyad}`.toLowerCase().includes(ogrenciArama.toLowerCase())).slice(0, 8).map(o => (
                                <div key={o.id} onClick={() => ogrenciSec(o)}
                                  style={{ padding: '7px 10px', cursor: 'pointer', fontSize: 12.5, borderBottom: `1px solid ${borderSub}` }}>
                                  {o.ad} {o.soyad}
                                </div>
                              ))}
                            </div>
                          )}
                          {biletForm.ogrenci_id && (
                            <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
                              <span style={{ background: '#EEF2FF', color: '#3730A3', padding: '4px 9px', borderRadius: 6, fontSize: 11.5, fontWeight: 600 }}>
                                ✓ {ogrenciArama} — {ogrenciTpBakiye} TP mevcut
                              </span>
                              {ogrenciTpBakiye > 0 && (
                                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, cursor: 'pointer', color: textPrimary }}>
                                  <input type="checkbox" checked={biletForm.tp_kullan} onChange={ev => setBiletForm(p => ({ ...p, tp_kullan: ev.target.checked }))} />
                                  Tüm TP'yi kullan
                                </label>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    {biletHata && <div style={{ color: '#EF4444', fontSize: 11, marginTop: 8 }}>{biletHata}</div>}
                    <button
                      onClick={biletKaydet} disabled={biletKaydiyor}
                      style={{ marginTop: 10, background: '#3C3489', color: 'white', border: 'none', borderRadius: 6, padding: '8px', fontSize: 12, cursor: 'pointer', width: '100%', fontWeight: 500 }}
                    >
                      {biletKaydiyor ? 'Kaydediliyor...' : 'Bileti Kaydet'}
                    </button>
                  </div>
                )}

                {biletYukleniyor ? (
                  <div style={{ textAlign: 'center', padding: 20, color: '#aaa', fontSize: 12 }}>Yükleniyor...</div>
                ) : biletler.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: 24, color: '#bbb', fontSize: 12 }}>Henüz bilet eklenmemiş</div>
                ) : (
                  <div>
                    {biletler.map(b => (
                      <div key={b.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 0', borderBottom: '1px solid #f5f5f5' }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 12, fontWeight: 500, color: '#333' }}>{b.ad} {b.soyad}</div>
                          <div style={{ fontSize: 11, color: '#888', marginTop: 1, display: 'flex', flexWrap: 'wrap', gap: '0 6px' }}>
                            {b.email && <span>{b.email}</span>}
                            {b.tel && <span>· {b.tel}</span>}
                            {b.tarih && <span>· {formatTarih(b.tarih)}</span>}
                            {b.tutar != null && <span>· {b.tutar} ₺</span>}
                            {b.ogrenci_id && <span style={{ color: '#3730A3' }}>· ⭐ TP eşleşti</span>}
                            {b.notlar && <span style={{ color: '#aaa' }}>· {b.notlar}</span>}
                          </div>
                        </div>
                        <span style={{
                          fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 10, flexShrink: 0,
                          background: ODEME_BG[b.odeme_durumu] || '#F3F4F6',
                          color: ODEME_RENK[b.odeme_durumu] || '#666',
                        }}>
                          {ODEME_LABEL[b.odeme_durumu] || b.odeme_durumu}
                        </span>
                        <button
                          onClick={() => biletSil(b.id)}
                          style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#EF4444', fontSize: 16, padding: '0 4px', lineHeight: 1, flexShrink: 0 }}
                          title="Sil"
                        >×</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div style={{ padding: '16px 20px' }}>
                <div style={{ color: '#aaa', fontSize: 12, fontStyle: 'italic' }}>Bu etkinlik için bilet satışı aktif değil.</div>
              </div>
            )}

            {/* Kasa Katılım Geçmişi — milonga/pratik etkinlikler için */}
            {secilen && ['milonga', 'pratik', 'festival'].includes(secilen.tip) && (
              <div style={{ padding: '16px 20px', borderTop: '1px solid #eee' }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#333', marginBottom: 12 }}>
                  Katılım Geçmişi
                  {katilimOzet && katilimOzet.seans_sayisi > 0 && (
                    <span style={{ marginLeft: 8, fontSize: 11, color: '#888', fontWeight: 400 }}>
                      {katilimOzet.seans_sayisi} seans
                    </span>
                  )}
                </div>

                {katilimOzet && katilimOzet.seans_sayisi > 0 && (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 12 }}>
                    {[
                      { label: 'Toplam Kişi', val: katilimOzet.toplam_kisi, renk: '#6B46C1', bg: '#F3F0FF' },
                      { label: 'Toplam Gelir', val: `₺${Number(katilimOzet.toplam_gelir).toLocaleString('tr-TR', { maximumFractionDigits: 0 })}`, renk: '#065F46', bg: '#D1FAE5' },
                      { label: 'Ort. Kişi', val: Math.round(katilimOzet.toplam_kisi / katilimOzet.seans_sayisi), renk: '#92400E', bg: '#FEF3C7' },
                    ].map(k => (
                      <div key={k.label} style={{ background: k.bg, borderRadius: 8, padding: '8px 10px' }}>
                        <div style={{ fontSize: 10, color: k.renk, fontWeight: 600, opacity: .7, marginBottom: 2 }}>{k.label}</div>
                        <div style={{ fontSize: 15, fontWeight: 700, color: k.renk }}>{k.val}</div>
                      </div>
                    ))}
                  </div>
                )}

                {katilimYukleniyor ? (
                  <div style={{ textAlign: 'center', padding: 20, color: '#aaa', fontSize: 12 }}>Yükleniyor...</div>
                ) : katilimlar.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: 16, color: '#bbb', fontSize: 12 }}>
                    Henüz kasa kaydı yok. Kasadan gelir girerken bu etkinliği seçin.
                  </div>
                ) : (
                  <div style={{ border: '1px solid #eee', borderRadius: 8, overflow: 'hidden' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                      <thead>
                        <tr style={{ background: '#fafafa', borderBottom: '1px solid #eee' }}>
                          {['Tarih', 'Kişi', 'Gelir', 'Açıklama'].map(h => (
                            <th key={h} style={{ textAlign: 'left', padding: '7px 10px', fontSize: 10, color: '#aaa', fontWeight: 600 }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {katilimlar.map((k, i) => (
                          <tr key={i} style={{ borderBottom: i < katilimlar.length - 1 ? '1px solid #f5f5f5' : 'none' }}>
                            <td style={{ padding: '7px 10px', color: '#555', whiteSpace: 'nowrap' }}>
                              {new Date(k.tarih).toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' })}
                            </td>
                            <td style={{ padding: '7px 10px' }}>
                              {k.bilet_sayisi
                                ? <span style={{ background: '#F3F0FF', color: '#6B46C1', borderRadius: 10, padding: '1px 7px', fontWeight: 600 }}>{k.bilet_sayisi}</span>
                                : <span style={{ color: '#ccc' }}>—</span>}
                            </td>
                            <td style={{ padding: '7px 10px', color: '#065F46', fontWeight: 500 }}>
                              ₺{Number(k.tutar).toLocaleString('tr-TR', { maximumFractionDigits: 0 })}
                            </td>
                            <td style={{ padding: '7px 10px', color: '#888', maxWidth: 100, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {k.aciklama || '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>
        </>
      )}

      {/* Form Modalı */}
      {modal && (
        <>
          <div
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 300 }}
            onClick={() => !kaydiyor && setModal(false)}
          />
          <div style={{
            position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%,-50%)',
            background: 'white', borderRadius: 12, padding: 28, zIndex: 301,
            width: 560, maxHeight: '90vh', overflow: 'auto',
          }}>
            <h3 style={{ margin: '0 0 20px', fontSize: 16, fontWeight: 700, color: '#222' }}>
              {duzenle ? 'Etkinliği Düzenle' : 'Yeni Etkinlik'}
            </h3>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <div style={{ gridColumn: '1/-1' }}>
                <label style={lblSt}>Etkinlik Adı *</label>
                <input style={inpSt} value={form.ad} onChange={ev => setF('ad', ev.target.value)} placeholder="Etkinlik adı" />
              </div>

              <div>
                <label style={lblSt}>Tip</label>
                <select style={inpSt} value={form.tip} onChange={ev => setF('tip', ev.target.value)}>
                  <option value="milonga">Milonga</option>
                  <option value="pratik">Pratik</option>
                  <option value="festival">Festival</option>
                  <option value="kiralama">Kiralama</option>
                  <option value="diger">Diğer</option>
                </select>
              </div>

              <div>
                <label style={lblSt}>Kapasite</label>
                <input style={inpSt} type="number" min="1" value={form.kapasite} onChange={ev => setF('kapasite', ev.target.value)} placeholder="Kişi sayısı (opsiyonel)" />
              </div>

              <div style={{ gridColumn: '1/-1' }}>
                <label style={lblSt}>Açıklama</label>
                <textarea
                  style={{ ...inpSt, height: 60, resize: 'vertical' } as React.CSSProperties}
                  value={form.aciklama}
                  onChange={ev => setF('aciklama', ev.target.value)}
                  placeholder="Opsiyonel"
                />
              </div>

              {/* Salon seçimi */}
              <div style={{ gridColumn: '1/-1' }}>
                <label style={lblSt}>Salonlar *</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {salonlar.map(s => (
                    <div
                      key={s.id}
                      onClick={() => toggleSalon(s.id)}
                      style={{
                        fontSize: 12, cursor: 'pointer', padding: '5px 12px', borderRadius: 6,
                        background: form.salon_idler.includes(s.id) ? '#EEEDFE' : '#f5f5f5',
                        color: form.salon_idler.includes(s.id) ? '#3C3489' : '#555',
                        border: `1px solid ${form.salon_idler.includes(s.id) ? '#3C3489' : '#e0e0e0'}`,
                        fontWeight: form.salon_idler.includes(s.id) ? 600 : 400,
                        userSelect: 'none',
                      }}
                    >
                      {s.ad}
                    </div>
                  ))}
                </div>
              </div>

              {/* Tekrarlayan toggle */}
              <div style={{ gridColumn: '1/-1' }}>
                <Toggle on={form.tekrarlayan} onChange={v => setF('tekrarlayan', v)} label="Sürekli / tekrarlayan etkinlik" />
              </div>

              {form.tekrarlayan ? (
                <div style={{ gridColumn: '1/-1' }}>
                  <label style={lblSt}>Günler *</label>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {GUNLER.map(g => (
                      <div
                        key={g}
                        onClick={() => toggleGun(g)}
                        style={{
                          fontSize: 12, cursor: 'pointer', padding: '5px 12px', borderRadius: 6,
                          background: form.gunler.includes(g) ? '#EEEDFE' : '#f5f5f5',
                          color: form.gunler.includes(g) ? '#3C3489' : '#555',
                          border: `1px solid ${form.gunler.includes(g) ? '#3C3489' : '#e0e0e0'}`,
                          fontWeight: form.gunler.includes(g) ? 600 : 400,
                          userSelect: 'none',
                        }}
                      >
                        {g}
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div style={{ gridColumn: '1/-1' }}>
                  <label style={lblSt}>Tarih *</label>
                  <input style={inpSt} type="date" value={form.tarih} onChange={ev => setF('tarih', ev.target.value)} />
                </div>
              )}

              {/* Tüm gün */}
              <div style={{ gridColumn: '1/-1' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, color: '#333', userSelect: 'none' }}>
                  <input
                    type="checkbox" checked={form.tum_gun}
                    onChange={ev => setF('tum_gun', ev.target.checked)}
                    style={{ width: 14, height: 14 }}
                  />
                  Tüm gün
                </label>
              </div>

              {!form.tum_gun && (
                <>
                  <div>
                    <label style={lblSt}>Başlangıç Saati</label>
                    <input style={inpSt} type="time" value={form.baslangic_saat} onChange={ev => setF('baslangic_saat', ev.target.value)} />
                  </div>
                  <div>
                    <label style={lblSt}>Bitiş Saati</label>
                    <input style={inpSt} type="time" value={form.bitis_saat} onChange={ev => setF('bitis_saat', ev.target.value)} />
                  </div>
                </>
              )}

              {/* Kiralama alanları */}
              {form.tip === 'kiralama' && (
                <>
                  <div>
                    <label style={lblSt}>Kiralayan</label>
                    <input style={inpSt} value={form.kiralayan} onChange={ev => setF('kiralayan', ev.target.value)} placeholder="Kiracı adı / kurum" />
                  </div>
                  <div>
                    <label style={lblSt}>Kira Ücreti (₺)</label>
                    <input style={inpSt} type="number" value={form.kira_ucreti} onChange={ev => setF('kira_ucreti', ev.target.value)} placeholder="Opsiyonel" />
                  </div>
                </>
              )}

              {/* Bilet satışı */}
              <div style={{ gridColumn: '1/-1' }}>
                <Toggle on={form.bilet_aktif} onChange={v => setF('bilet_aktif', v)} label="Bilet satışı aktif" />
              </div>

              {form.bilet_aktif && (
                <div>
                  <label style={lblSt}>Bilet Fiyatı (₺)</label>
                  <input style={inpSt} type="number" value={form.bilet_fiyati} onChange={ev => setF('bilet_fiyati', ev.target.value)} placeholder="Opsiyonel" />
                </div>
              )}
            </div>

            {hata && (
              <div style={{ color: '#EF4444', fontSize: 12, marginTop: 14, padding: '8px 10px', background: '#FEF2F2', borderRadius: 6 }}>
                {hata}
              </div>
            )}

            {cakisma.length > 0 && (
              <div style={{ marginTop: 14, background: '#FFF7ED', border: '1px solid #FED7AA', borderRadius: 8, padding: 14 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#EA580C', marginBottom: 8 }}>⚠️ Salon çakışması tespit edildi:</div>
                {cakisma.map((c, i) => (
                  <div key={i} style={{ fontSize: 11, color: '#7C2D12', marginBottom: 3 }}>• {c}</div>
                ))}
                <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                  <button
                    onClick={() => setCakisma([])}
                    style={{ flex: 1, border: '1px solid #ddd', background: 'white', borderRadius: 6, padding: 8, fontSize: 12, cursor: 'pointer' }}
                  >Geri Dön</button>
                  <button
                    onClick={() => kaydet(true)} disabled={kaydiyor}
                    style={{ flex: 1, border: 'none', background: '#EA580C', color: 'white', borderRadius: 6, padding: 8, fontSize: 12, cursor: 'pointer', fontWeight: 500 }}
                  >Yine de Kaydet</button>
                </div>
              </div>
            )}

            {cakisma.length === 0 && (
              <div style={{ display: 'flex', gap: 10, marginTop: 22 }}>
                <button
                  onClick={() => setModal(false)} disabled={kaydiyor}
                  style={{ flex: 1, border: '1px solid #ddd', background: 'white', borderRadius: 8, padding: 10, fontSize: 13, cursor: 'pointer', color: '#555' }}
                >İptal</button>
                <button
                  onClick={() => kaydet(false)} disabled={kaydiyor}
                  style={{ flex: 2, border: 'none', background: '#3C3489', color: 'white', borderRadius: 8, padding: 10, fontSize: 13, cursor: 'pointer', fontWeight: 500 }}
                >
                  {kaydiyor ? 'Kaydediliyor...' : (duzenle ? 'Güncelle' : 'Etkinlik Ekle')}
                </button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default Etkinlikler;
